import { useCallback, useEffect, useRef, useState } from "react";

export type ProTableAutoHeightMetrics = {
  viewportHeight: number;
  searchAreaHeight: number;
};

type UseProTableAutoHeightOptions = {
  autoHeight: boolean;
  getSearchNode: () => HTMLElement | null;
  searchEnabled: boolean;
};

type AutoHeightUpdateSource = "initial" | "window-resize" | "search-resize";

const SEARCH_RESIZE_SETTLE_DELAY = 120;

export function getInitialProTableAutoHeightMetrics(): ProTableAutoHeightMetrics {
  return {
    viewportHeight: typeof window !== "undefined" ? window.innerHeight : 900,
    searchAreaHeight: 0,
  };
}

export function buildProTableAutoHeightMetricsUpdate(
  current: ProTableAutoHeightMetrics,
  next: ProTableAutoHeightMetrics,
) {
  if (
    current.viewportHeight === next.viewportHeight &&
    current.searchAreaHeight === next.searchAreaHeight
  ) {
    return undefined;
  }
  return next;
}

export function resolveResizeObserverHeight(entry?: ResizeObserverEntry) {
  const height = entry?.contentRect.height;
  return typeof height === "number" ? Math.ceil(height) : undefined;
}

export function resolveProTableAutoHeightCommitDelay(
  source: AutoHeightUpdateSource,
) {
  return source === "search-resize" ? SEARCH_RESIZE_SETTLE_DELAY : 0;
}

export function useProTableAutoHeight({
  autoHeight,
  getSearchNode,
  searchEnabled,
}: UseProTableAutoHeightOptions) {
  const [metrics, setMetrics] = useState<ProTableAutoHeightMetrics>(
    getInitialProTableAutoHeightMetrics,
  );
  const metricsRef = useRef(metrics);
  const frameRef = useRef<number>();
  const settleTimerRef = useRef<number>();
  const pendingSearchAreaHeightRef = useRef<number>();

  const readSearchAreaHeight = useCallback(() => {
    const searchNode = getSearchNode();
    return searchNode ? Math.ceil(searchNode.getBoundingClientRect().height) : 0;
  }, [getSearchNode]);

  const commitMetrics = useCallback((next: ProTableAutoHeightMetrics) => {
    const update = buildProTableAutoHeightMetricsUpdate(
      metricsRef.current,
      next,
    );
    if (!update) return;

    metricsRef.current = update;
    setMetrics(update);
  }, []);

  const flushMetrics = useCallback(() => {
    frameRef.current = undefined;
    const pendingSearchAreaHeight = pendingSearchAreaHeightRef.current;
    pendingSearchAreaHeightRef.current = undefined;

    commitMetrics({
      viewportHeight: typeof window !== "undefined" ? window.innerHeight : 900,
      searchAreaHeight:
        typeof pendingSearchAreaHeight === "number"
          ? pendingSearchAreaHeight
          : readSearchAreaHeight(),
    });
  }, [commitMetrics, readSearchAreaHeight]);

  const scheduleMetricsFrame = useCallback(() => {
    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      flushMetrics();
      return;
    }

    if (frameRef.current !== undefined) return;
    frameRef.current = window.requestAnimationFrame(flushMetrics);
  }, [flushMetrics]);

  const scheduleMetricsUpdate = useCallback(
    (
      source: AutoHeightUpdateSource,
      observedSearchAreaHeight?: number,
    ) => {
      if (typeof observedSearchAreaHeight === "number") {
        pendingSearchAreaHeightRef.current = Math.ceil(observedSearchAreaHeight);
      }

      if (typeof window === "undefined") {
        scheduleMetricsFrame();
        return;
      }

      const delay = resolveProTableAutoHeightCommitDelay(source);
      if (delay <= 0) {
        if (settleTimerRef.current !== undefined) {
          window.clearTimeout(settleTimerRef.current);
          settleTimerRef.current = undefined;
        }
        scheduleMetricsFrame();
        return;
      }

      if (settleTimerRef.current !== undefined) {
        window.clearTimeout(settleTimerRef.current);
      }
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = undefined;
        scheduleMetricsFrame();
      }, delay);
    },
    [scheduleMetricsFrame],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const clearScheduledMetrics = () => {
      if (
        frameRef.current !== undefined &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(frameRef.current);
      }
      if (settleTimerRef.current !== undefined) {
        window.clearTimeout(settleTimerRef.current);
      }
      frameRef.current = undefined;
      settleTimerRef.current = undefined;
      pendingSearchAreaHeightRef.current = undefined;
    };

    if (!autoHeight) {
      clearScheduledMetrics();
      commitMetrics({
        viewportHeight: window.innerHeight,
        searchAreaHeight: 0,
      });
      return;
    }

    scheduleMetricsUpdate("initial");

    const searchNode = getSearchNode();
    const resizeObserver =
      searchNode && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver((entries) => {
            scheduleMetricsUpdate(
              "search-resize",
              resolveResizeObserverHeight(entries[0]),
            );
          })
        : null;

    if (resizeObserver && searchNode) {
      resizeObserver.observe(searchNode);
    }

    const handleWindowResize = () => {
      scheduleMetricsUpdate("window-resize");
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      clearScheduledMetrics();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [
    autoHeight,
    searchEnabled,
    getSearchNode,
    commitMetrics,
    scheduleMetricsUpdate,
  ]);

  return metrics;
}
